type MeLikeResponse = {
  authenticated?: boolean;
  user?: any | null;
  user_id?: number;
  id_user?: number;
  email?: string | null;
  linked_providers?: Array<{
    provider_name?: string;
    issuer?: string;
    email_at_link?: string | null;
    linked_at?: string;
  }>;
  display_name?: string | null;
  name?: string | null;
};

const GENERIC_IDENTITY_LABELS = new Set([
  'pac account',
  'signed in user',
  'oidc_user',
]);

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function firstNonEmpty(...values: unknown[]): string | null {
  for (const value of values) {
    if (hasText(value)) return value.trim();
  }
  return null;
}

function isGenericIdentity(value: unknown): boolean {
  if (!hasText(value)) return true;
  return GENERIC_IDENTITY_LABELS.has(value.trim().toLowerCase());
}

export function normalizeAuthUser(raw: MeLikeResponse | any): any | null {
  if (!raw || raw.authenticated === false) return null;

  const user = raw?.user && typeof raw.user === 'object' ? raw.user : null;

  const idUser =
    typeof user?.id_user === 'number'
      ? user.id_user
      : typeof user?.user_id === 'number'
        ? user.user_id
        : typeof raw?.user_id === 'number'
      ? raw.user_id
      : typeof raw?.id_user === 'number'
        ? raw.id_user
        : null;

  const linkedProviders = Array.isArray(user?.linked_providers)
    ? user.linked_providers
    : Array.isArray(raw?.linked_providers)
      ? raw.linked_providers
      : [];
  const firstProvider = linkedProviders.find((provider: any) => provider && typeof provider === 'object') ?? null;
  const email = firstNonEmpty(
    user?.email,
    raw?.email,
    firstProvider?.email_at_link,
  );

  const fullName = firstNonEmpty(user?.full_name, raw?.full_name);
  const language = firstNonEmpty(user?.preferences?.language, raw?.preferences?.language, user?.language, raw?.language, user?.lang, raw?.lang);
  const preferences = {
    ...(user?.preferences && typeof user.preferences === 'object' ? user.preferences : {}),
    ...(raw?.preferences && typeof raw.preferences === 'object' ? raw.preferences : {}),
    ...(language ? { language } : {}),
  };
  const displayNameCandidate = firstNonEmpty(
    user?.display_name,
    raw?.display_name,
    user?.name,
    raw?.name,
  );
  const displayName = !isGenericIdentity(displayNameCandidate)
    ? displayNameCandidate
    : (email ?? (idUser != null ? `PAC user #${idUser}` : 'PAC account'));
  const name = !isGenericIdentity(firstNonEmpty(user?.name, raw?.name))
    ? firstNonEmpty(user?.name, raw?.name)
    : (displayName ?? email ?? (idUser != null ? `PAC user #${idUser}` : 'PAC account'));

  if (user) {
    return {
      ...user,
      authenticated: raw?.authenticated !== false,
      ...(idUser != null ? { id_user: idUser, user_id: idUser } : {}),
      ...(email ? { email } : {}),
      ...(fullName ? { full_name: fullName } : {}),
      ...(Object.keys(preferences).length > 0 ? { preferences } : {}),
      ...(language ? { language, lang: language } : {}),
      display_name: displayName,
      name,
      linked_providers: linkedProviders,
    };
  }

  return {
    authenticated: raw?.authenticated !== false,
    id_user: idUser,
    user_id: idUser,
    email,
    ...(fullName ? { full_name: fullName } : {}),
    ...(Object.keys(preferences).length > 0 ? { preferences } : {}),
    ...(language ? { language, lang: language } : {}),
    display_name: displayName,
    name,
    linked_providers: linkedProviders,
  };
}
